import React, { useContext, useMemo } from 'react';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { FilterName } from 'types/enums';
import { Instrument, Genre, Sfx, BaseMeta } from 'types';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import getListOfItems from './helpers/getListOfItems';
import { FiltersContextType, ListOfItems } from './types';

export const DefaultFiltersContext: FiltersContextType = {
    listOfInstruments: undefined,
    listOfGenres: undefined,
    listOfSfxs: undefined,
    listOfTypes: undefined,
    listOfTags: undefined,
};

export const FiltersContext = React.createContext<FiltersContextType>(
    DefaultFiltersContext,
);

export const useFiltersContext = (): FiltersContextType =>
    useContext(FiltersContext);

export const FiltersProvider = ({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement => {
    const { apiFilters } = useApiFiltersContext();
    const {
        filterTypesMeta,
        filterInstrumentsMeta,
        filterGenresMeta,
        filterSfxsMeta,
        filterTagsMeta,
    } = useSearchController();

    const listOfInstruments: ListOfItems = useMemo(
        () =>
            getListOfItems<Instrument>({
                apiFilters,
                filterName: FilterName.Instruments,
                searchFilterMeta: filterInstrumentsMeta,
                subKey: 'subInstruments',
            }),
        [apiFilters, filterInstrumentsMeta],
    );

    const listOfGenres: ListOfItems = useMemo(
        () =>
            getListOfItems<Genre>({
                apiFilters,
                filterName: FilterName.Genres,
                searchFilterMeta: filterGenresMeta,
                subKey: 'subGenres',
            }),
        [apiFilters, filterGenresMeta],
    );

    const listOfSfxs: ListOfItems = useMemo(
        () =>
            getListOfItems<Sfx>({
                apiFilters,
                filterName: FilterName.Sfxs,
                searchFilterMeta: filterSfxsMeta,
                subKey: 'subSfxs',
            }),
        [apiFilters, filterSfxsMeta],
    );

    const listOfTags: ListOfItems = useMemo(
        () =>
            getListOfItems<BaseMeta>({
                apiFilters,
                filterName: FilterName.Tags,
                searchFilterMeta: filterTagsMeta,
            }),
        [apiFilters, filterTagsMeta],
    );

    const listOfTypes: ListOfItems = useMemo(
        () =>
            getListOfItems<BaseMeta>({
                apiFilters,
                filterName: FilterName.Type,
                searchFilterMeta: filterTypesMeta,
                isReversed: true,
            }),
        [apiFilters, filterTypesMeta],
    );

    const context = {
        listOfInstruments: listOfInstruments,
        listOfGenres: listOfGenres,
        listOfSfxs: listOfSfxs,
        listOfTypes: listOfTypes,
        listOfTags: listOfTags,
    };

    return (
        <FiltersContext.Provider value={context}>
            {children}
        </FiltersContext.Provider>
    );
};

export default FiltersProvider;
